import React, { useState } from 'react';
import { Card, Table, Tag, Button, Space, Drawer, Descriptions, Typography, Select, DatePicker, Alert, Tooltip } from 'antd';
import {
  EyeOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

const { Text, Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const AutomationLogsTable = ({ logs, pagination, onPageChange, onRetry, onViewDetails, isLoading }) => {
  const { t } = useTranslation(['tables', 'forms', 'messages']);
  const pa = (key) => t(`payrollAutomation.${key}`, { ns: 'tables' });

  const [selectedLog, setSelectedLog] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({
    status: undefined,
    ruleType: undefined,
    dateRange: undefined,
  });

  const STATUS_CONFIG = {
    SUCCESS: {
      color: 'success',
      icon: <CheckCircleOutlined />,
      label: pa('success'),
    },
    FAILED: {
      color: 'error',
      icon: <CloseCircleOutlined />,
      label: pa('failed'),
    },
    RUNNING: {
      color: 'processing',
      icon: <SyncOutlined spin />,
      label: pa('runningStatus'),
    },
    PENDING: {
      color: 'default',
      icon: <ClockCircleOutlined />,
      label: pa('pending'),
    },
  };

  const RULE_TYPES = [
    { value: 'AUTO_CALCULATE_OVERTIME', label: pa('autoCalculateOvertime') },
    { value: 'AUTO_SUBMIT_TIMESHEET', label: pa('autoSubmitTimesheet') },
    { value: 'AUTO_GENERATE_SALARY', label: pa('autoGenerateSalary') },
    { value: 'AUTO_SEND_REMINDERS', label: pa('autoSendReminders') },
    { value: 'AUTO_VALIDATE_TIMESHEETS', label: pa('autoValidateTimesheets') },
  ];

  const TRIGGER_LABELS = {
    SCHEDULE: pa('scheduled'),
    MANUAL: pa('manual'),
    EVENT: pa('event'),
    USER: pa('userAction'),
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setDrawerVisible(true);
    if (onViewDetails) {
      onViewDetails(log._id);
    }
  };

  const handleRetry = (log) => {
    if (onRetry) {
      onRetry(log._id);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const columns = [
    {
      title: pa('executionTime'),
      dataIndex: 'executedAt',
      key: 'executedAt',
      width: 180,
      render: (executedAt) => (
        <div>
          <div>{dayjs(executedAt).format('DD.MM.YYYY')}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(executedAt).format('HH:mm:ss')}
          </Text>
        </div>
      ),
      sorter: true,
    },
    {
      title: pa('ruleType'),
      dataIndex: 'ruleType',
      key: 'ruleType',
      render: (ruleType) => {
        const rule = RULE_TYPES.find(r => r.value === ruleType);
        return (
          <Tag color="blue">
            {rule?.label || ruleType}
          </Tag>
        );
      },
      filters: RULE_TYPES.map(r => ({ text: r.label, value: r.value })),
      filterMultiple: false,
    },
    {
      title: pa('trigger'),
      dataIndex: ['triggerInfo', 'type'],
      key: 'triggerType',
      width: 120,
      render: (type, record) => (
        <Tooltip title={record.triggerInfo?.userId ? `${pa('userAction')}: ${record.triggerInfo.userId}` : ''}>
          <Tag>{TRIGGER_LABELS[type] || type}</Tag>
        </Tooltip>
      ),
    },
    {
      title: pa('duration'),
      key: 'duration',
      width: 100,
      render: (_, record) => {
        if (!record.startTime) return '-';
        const end = record.endTime ? dayjs(record.endTime) : dayjs();
        const start = dayjs(record.startTime);
        const durationMs = end.diff(start);
        const dur = dayjs.duration(durationMs);

        if (durationMs < 1000) {
          return `${durationMs}ms`;
        } else if (durationMs < 60000) {
          return `${dur.seconds()}s`;
        } else {
          return `${dur.minutes()}m ${dur.seconds()}s`;
        }
      },
    },
    {
      title: t('status', { ns: 'tables' }),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
        return (
          <Tag icon={config.icon} color={config.color}>
            {config.label}
          </Tag>
        );
      },
      filters: Object.entries(STATUS_CONFIG).map(([key, config]) => ({
        text: config.label,
        value: key,
      })),
      filterMultiple: false,
    },
    {
      title: pa('result'),
      dataIndex: 'result',
      key: 'result',
      ellipsis: true,
      render: (result) => {
        if (!result) return '-';
        if (typeof result === 'object') {
          const summary = result.summary || result.message || JSON.stringify(result);
          return (
            <Tooltip title={summary}>
              <Text ellipsis style={{ maxWidth: 200 }}>
                {summary}
              </Text>
            </Tooltip>
          );
        }
        return result;
      },
    },
    {
      title: t('actions', { ns: 'tables' }),
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            {pa('view')}
          </Button>
          {record.status === 'FAILED' && (
            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => handleRetry(record)}
            >
              {pa('retry')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={pa('automationExecutionLogs')}
        extra={
          <Space>
            <Select
              placeholder={pa('filterByStatus')}
              style={{ width: 140 }}
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
            >
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <Option key={key} value={key}>
                  {config.label}
                </Option>
              ))}
            </Select>
            <Select
              placeholder={pa('filterByRule')}
              style={{ width: 180 }}
              allowClear
              value={filters.ruleType}
              onChange={(value) => handleFilterChange('ruleType', value)}
            >
              {RULE_TYPES.map(rule => (
                <Option key={rule.value} value={rule.value}>
                  {rule.label}
                </Option>
              ))}
            </Select>
            <RangePicker
              style={{ width: 240 }}
              value={filters.dateRange}
              onChange={(value) => handleFilterChange('dateRange', value)}
            />
          </Space>
        }
        style={{ marginBottom: 24 }}
        loading={isLoading}
      >
        <Table
          dataSource={logs}
          columns={columns}
          rowKey="_id"
          pagination={{
            ...pagination,
            onChange: onPageChange,
            showSizeChanger: true,
            showTotal: (total) => pa('totalRecords').replace('{{total}}', total),
          }}
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* Details Drawer */}
      <Drawer
        title={pa('automationExecutionDetails')}
        placement="right"
        width={720}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        {selectedLog && (
          <div>
            {/* Status Alert */}
            {selectedLog.status === 'FAILED' && (
              <Alert
                message={pa('executionFailed')}
                description={selectedLog.error?.message || pa('errorMessage')}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                action={
                  <Button size="small" danger onClick={() => handleRetry(selectedLog)}>
                    {pa('retry')}
                  </Button>
                }
              />
            )}

            {selectedLog.status === 'SUCCESS' && (
              <Alert
                message={pa('executionSuccessful')}
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Basic Information */}
            <Descriptions title={pa('basicInformation')} bordered column={1} size="small" style={{ marginBottom: 24 }}>
              <Descriptions.Item label={pa('executionId')}>
                {selectedLog._id}
              </Descriptions.Item>
              <Descriptions.Item label={pa('ruleType')}>
                <Tag color="blue">
                  {RULE_TYPES.find(r => r.value === selectedLog.ruleType)?.label || selectedLog.ruleType}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('status', { ns: 'tables' })}>
                <Tag icon={STATUS_CONFIG[selectedLog.status]?.icon} color={STATUS_CONFIG[selectedLog.status]?.color}>
                  {STATUS_CONFIG[selectedLog.status]?.label}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={pa('triggerType')}>
                {TRIGGER_LABELS[selectedLog.triggerInfo?.type] || selectedLog.triggerInfo?.type}
              </Descriptions.Item>
              <Descriptions.Item label={pa('executedAt')}>
                {dayjs(selectedLog.executedAt).format('DD.MM.YYYY HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label={pa('duration')}>
                {selectedLog.startTime && selectedLog.endTime
                  ? `${dayjs(selectedLog.endTime).diff(dayjs(selectedLog.startTime), 'second')}s`
                  : '-'}
              </Descriptions.Item>
            </Descriptions>

            {/* Input Data */}
            {selectedLog.inputData && Object.keys(selectedLog.inputData).length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>{pa('inputData')}</Title>
                <pre style={{
                  backgroundColor: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  overflow: 'auto',
                  fontSize: 12,
                }}>
                  {JSON.stringify(selectedLog.inputData, null, 2)}
                </pre>
              </div>
            )}

            {/* Result */}
            {selectedLog.result && (
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>{pa('result')}</Title>
                <pre style={{
                  backgroundColor: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  overflow: 'auto',
                  fontSize: 12,
                }}>
                  {typeof selectedLog.result === 'string'
                    ? selectedLog.result
                    : JSON.stringify(selectedLog.result, null, 2)}
                </pre>
              </div>
            )}

            {/* Error Details */}
            {selectedLog.error && (
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>{pa('errorDetails')}</Title>
                <Alert
                  message={selectedLog.error.message}
                  description={
                    <div>
                      {selectedLog.error.code && (
                        <div><strong>Code:</strong> {selectedLog.error.code}</div>
                      )}
                      {selectedLog.error.stack && (
                        <pre style={{
                          marginTop: 8,
                          fontSize: 11,
                          backgroundColor: '#fff',
                          padding: 8,
                          borderRadius: 4,
                          overflow: 'auto',
                        }}>
                          {selectedLog.error.stack}
                        </pre>
                      )}
                    </div>
                  }
                  type="error"
                  showIcon
                />
              </div>
            )}

            {/* Metadata */}
            {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
              <div>
                <Title level={5}>{pa('metadata')}</Title>
                <Descriptions bordered column={1} size="small">
                  {Object.entries(selectedLog.metadata).map(([key, value]) => (
                    <Descriptions.Item key={key} label={key}>
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </>
  );
};

export default AutomationLogsTable;
